import { useMapContext } from '@components/map/MapContext';
import { useAppSelector } from '@hooks/redux.hooks';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { updateDrawPolygonsData } from '@redux/maps/maps.reducer';
import { selectMapEditPolygonsInfo } from '@redux/maps/maps.selectors';
import { colors } from '@theme/foundations/colors';
import { Polygon } from 'geojson';

import { MapBoxDrawEvent } from '@/types/map.types';

import { DrawPolygonControl } from './DrawPolygonControl';

export const drawStyle = [
  // line stroke
  {
    id: 'gl-draw-line',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': colors.blue[600].value,
      'line-dasharray': [0.2, 2],
      'line-width': 5,
    },
  },
  // polygon fill
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': colors.blue[600].value,
      'fill-outline-color': colors.blue[600].value,
      'fill-opacity': 0.1,
    },
  },
  // polygon mid points
  {
    id: 'gl-draw-polygon-midpoint',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: {
      'circle-radius': 9,
      'circle-color': colors.skyBlue[500].value,
    },
  },
  // polygon outline stroke
  // This doesn't style the first edge of the polygon, which uses the line stroke styling instead
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'Polygon']],
    layout: {
      'line-cap': 'round',
      'line-join': 'round',
    },
    paint: {
      'line-color': colors.blue[600].value,
      'line-dasharray': [0.2, 2],
      'line-width': 5,
    },
  },
  // vertex point halos
  {
    id: 'gl-draw-polygon-and-line-vertex-halo-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 11,
      'circle-color': colors.neutral[900].value,
    },
  },
  // vertex points
  {
    id: 'gl-draw-polygon-and-line-vertex-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 9,
      'circle-color': colors.blue[600].value,
    },
  },
  // point
  {
    id: 'gl-draw-point-active',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 9,
      'circle-color': colors.skyBlue[500].value,
    },
  },
  {
    id: 'gl-draw-point-inactive',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point']],
    paint: {
      'circle-radius': 9,
      'circle-color': colors.blue[500].value,
    },
  },
];

export function DrawPolygonLayer() {
  const { mapId } = useMapContext();
  const dispatchUpdateDrawPolygonsData = useWithDispatch(updateDrawPolygonsData);
  const polyToEdit = useAppSelector((state) => selectMapEditPolygonsInfo(state, mapId));

  const onCreateDraw = (evt: { features: MapBoxDrawEvent[] }) => {
    dispatchUpdateDrawPolygonsData({
      mapId,
      points: evt.features[0].geometry as Polygon,
    });
  };

  const onUpdateDraw = (evt: { features: MapBoxDrawEvent[]; action: string }) => {
    dispatchUpdateDrawPolygonsData({
      mapId,
      points: evt.features[0].geometry as Polygon,
    });
  };

  // const mainDrawColor = colors.cyber[600];
  // const secondDrawColor = colors.sky[500];

  return (
    <DrawPolygonControl
      position="top-left"
      displayControlsDefault={false}
      controls={{
        point: false,
        line_string: false,
        polygon: false,
        trash: false,
        combine_features: false,
        uncombine_features: false,
      }}
      defaultMode="draw_polygon"
      styles={drawStyle}
      onCreate={onCreateDraw}
      onUpdate={onUpdateDraw}
      zoneToEdit={polyToEdit}
    />
  );
}
